import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  RgbaColor,
  selectEnvironmentInteractionPoints,
} from "../../app/slices/EnvironmentSlice";
import {
  closeSequencePositionPicker,
  selectSequencePositionPicker,
  selectSelectedTab,
  SelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import { ISetCamera } from "../../models/interactions/SetCamera";
import { ISequence } from "../../models/interactions/Sequence";
import { InteractionType } from "../../models/interactions/Interaction";
import useUpdateSequenceInteraction from "../sequence/node_previews/UpdateSequenceInteraction.hook";
import InteractionPointPreview, {
  CameraPointStrategy,
  SelectedCameraPointStrategy,
} from "./InteractionPointPreview";
import { getEnvironmentPositionContexts } from "../sequence/EnvironmentPositionContext";

const selectedMarkerColor: RgbaColor = {
  r: 58 / 255,
  g: 196 / 255,
  b: 1,
  a: 1,
};

export default function Viewport3DSequenceCameraPositionLayer() {
  const dispatch = useDispatch();
  const currentTab = useSelector(selectSelectedTab);
  const picker = useSelector(selectSequencePositionPicker);
  const selectedElementType = useSelector(selectSelectedElementType);
  const sequence = useSelector(selectSelectedElement) as ISequence | undefined;
  const currentTake = useSelector(selectSelectedTake3D);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const { getSituationMetadata } = useSituationHelper();
  const { updateInteraction } = useUpdateSequenceInteraction();

  const activeInteraction = useMemo(() => {
    return sequence?.nodes?.find((node) => node.id === picker.nodeId)?.data
      ?.interaction as ISetCamera | undefined;
  }, [picker.nodeId, sequence?.nodes]);

  const isSequenceContext =
    selectedElementType === "takeSequence" ||
    (selectedElementType === "interaction" &&
      sequence?.type === InteractionType.Sequence);

  const isVisible =
    currentTab === SelectedTab.Timeline &&
    isSequenceContext &&
    picker.isOpen &&
    picker.positionType === "camera" &&
    !!activeInteraction &&
    activeInteraction.type === InteractionType.SetCamera;

  const pickableCameraPositions = useMemo(() => {
    return getEnvironmentPositionContexts(
      takePositions ?? [],
      currentTake?.situations,
      getSituationMetadata,
    ).flatMap((context) =>
      context.cameraPositions.map((cameraPosition) => ({
        cameraPosition,
        takeColor: context.takeColor,
        takeLocationId: context.takeLocationId,
      })),
    );
  }, [currentTake?.situations, getSituationMetadata, takePositions]);

  if (!isVisible || !activeInteraction) {
    return null;
  }

  const selectedCameraPositionId = activeInteraction.cameraId;

  const getStrategyForCamera = (isSelected: boolean) => {
    return isSelected ? SelectedCameraPointStrategy : CameraPointStrategy;
  };

  const getMarkerBorderColor = (isSelected: boolean, takeColor?: RgbaColor) => {
    return isSelected ? selectedMarkerColor : takeColor;
  };

  const getMarkerBackgroundColor = (
    isSelected: boolean,
    takeColor?: RgbaColor,
  ) => {
    return isSelected ? selectedMarkerColor : undefined;
  };

  const getMarkerIconColor = (isSelected: boolean) => {
    return isSelected ? { r: 1, g: 1, b: 1, a: 1 } : undefined;
  };

  return (
    <>
      {pickableCameraPositions.map(
        ({ cameraPosition, takeColor, takeLocationId }) => {
          const isSelected =
            selectedCameraPositionId === cameraPosition.id &&
            (!activeInteraction.takeLocationId ||
              activeInteraction.takeLocationId === takeLocationId);

          return (
            <InteractionPointPreview
              key={`${takeLocationId}/${cameraPosition.id}`}
              strategy={getStrategyForCamera(isSelected)}
              x={cameraPosition.position.x}
              y={cameraPosition.position.y}
              markerBorderColor={getMarkerBorderColor(isSelected, takeColor)}
              markerBackgroundColor={getMarkerBackgroundColor(
                isSelected,
                takeColor,
              )}
              markerIconColor={getMarkerIconColor(isSelected)}
              directionDegrees={cameraPosition.direction?.x}
              directionColor={getMarkerBorderColor(isSelected, takeColor)}
              onClick={() => {
                updateInteraction(picker.nodeId, {
                  ...activeInteraction,
                  cameraId: cameraPosition.id,
                  takeLocationId,
                } as ISetCamera);
                dispatch(closeSequencePositionPicker());
              }}
            />
          );
        },
      )}
    </>
  );
}
